import { useState, useCallback, useEffect } from 'react'
import api from '../../../services/api'

const useDateFilter = ({ tableFilter, dateFrom, dateTo }) => {
  // User table state
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [userData, setUserData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(0)

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleSearch = useCallback((value) => {
    setSearchTerm(value)
    setOffset(0)
  }, [])

  // Reset offset when external filters change
  useEffect(() => {
    setOffset(0)
  }, [tableFilter, dateFrom, dateTo])

  const handlePageChange = useCallback((page, pageSize) => {
    setOffset((page - 1) * pageSize)
    setLimit(pageSize)
  }, [])

  const fetchUsers = useCallback(async () => {
    if (tableFilter === 'other' && (!dateFrom || !dateTo)) {
      setUserData([])
      setTotal(0)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await api.aiCredits.getUsageReportCreditsService({
        offset,
        limit,
        search: debouncedSearchTerm,
        date: tableFilter,
        from: dateFrom,
        to: dateTo
      })

      const responseData = response?.data || {}
      // Check if data is nested under another data property
      const effectiveData = responseData.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)
        ? responseData.data
        : responseData

      // Use users or fall back to an array at root
      const rawRecords = effectiveData.users || (Array.isArray(effectiveData) ? effectiveData : [])

      const mappedData = Array.isArray(rawRecords) ? rawRecords.map((item, index) => {
        const creatorName = item.creator
          ? `${item.creator.first_name || ''} ${item.creator.last_name || ''}`.trim()
          : null

        const displayName = creatorName || item.title || item.name || item.full_name || 'Unknown'
        const displayAvatar = item.creator?.avatar?.link || item.avatar || item.profile_image || ''

        return {
          id: item.id || item.user_id,
          name: displayName,
          rank: offset + index + 1,
          avatar: displayAvatar,
          totalCreditsUsed: item.total_used_credits || item.used_credits || 0,
          level: item.title ? `Pages: ${item.pages || 0}` : (item.level || 'Member')
        }
      }) : []

      setUserData(mappedData)
      setTotal(effectiveData.count || effectiveData.total || responseData.count || responseData.total || mappedData.length)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching credit usage data:', err)
      setError(err.message || 'Failed to fetch data')
      setLoading(false)
    }
  }, [tableFilter, dateFrom, dateTo, debouncedSearchTerm, offset, limit])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  return {
    // User table data and handlers
    userData,
    userLoading: loading,
    userError: error,
    userTotal: total,
    searchTerm,
    pagination: {
      current: Math.floor(offset / limit) + 1,
      pageSize: limit,
      total: total
    },
    onSearch: handleSearch,
    onPageChange: handlePageChange,
    refetchUsers: fetchUsers
  }
}

export default useDateFilter
